import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { tap } from 'rxjs/operators';
import { AuthService } from './auth.service';

export interface UserProfile {
  id: string;
  name: string;
  admissionDate: string;
  bornDate: string;
  roleDescription: string;
  username: string;
  email: string;
  company: string;
  avatar: string;
}

const emptyProfile: UserProfile = {
  id: '',
  name: '',
  admissionDate: '',
  bornDate: '',
  roleDescription: '',
  username: '',
  email: '',
  company: '',
  avatar: ''
};

@Injectable({
  providedIn: 'root'
})
export class UserProfileService {
  private readonly profileSubject = new BehaviorSubject<UserProfile>(emptyProfile);

  readonly profile$ = this.profileSubject.asObservable();

  constructor(private auth: AuthService) { }

  loadSummary(force = false): Observable<UserProfile> {
    const current = this.profileSubject.value;
    if (!force && current.name) {
      return of(current);
    }

    const claims = this.auth.getTokenClaims();
    const username = this.firstString(
      claims['preferred_username'],
      claims['username'],
      claims['unique_name'],
      claims.sub,
      localStorage.getItem('remembered_login')
    );
    const name = this.firstString(
      claims['name'],
      claims['given_name'],
      this.auth.getFirstName(),
      username
    );
    const profile: UserProfile = {
      ...this.profileSubject.value,
      id: this.firstString(claims['employee_id'], claims['registration'], claims.sub),
      name,
      admissionDate: this.firstString(claims['admission_date']),
      bornDate: this.firstString(claims['birthdate']),
      roleDescription: this.firstString(
        claims['role_description'],
        claims['job_title'],
        claims['role']
      ),
      username,
      email: this.firstString(claims['email']),
      company: this.firstString(claims['company'], claims['organization']),
    };

    return of(profile).pipe(
      tap(value => {
        this.profileSubject.next(value);
        this.auth.setFirstName(value.name);
      })
    );
  }

  loadAvatar(_force = false): Observable<string> {
    return of('');
  }

  reset(): void {
    this.profileSubject.next(emptyProfile);
  }

  private firstString(...values: unknown[]): string {
    const value = values.find(item =>
      typeof item === 'string' && item.trim().length > 0
    );
    return typeof value === 'string' ? value.trim() : '';
  }
}
